import { Result } from '../../models/data.models';
import { normalizeResultDiscipline } from '../../models/discipline.models';
import { normalizeStandingCategory } from '../../models/category-standings.models';
import { medalRank } from '../../models/podium.models';

export type MemberAgeGroup = 'BB' | 'E' | 'P' | 'B' | 'M' | 'C' | 'J' | 'S' | 'V1' | 'V2' | 'V3';

/** Age groups from the youngest to the oldest, for display. */
export const MEMBER_AGE_GROUPS: MemberAgeGroup[] = ['BB', 'E', 'P', 'B', 'M', 'C', 'J', 'S', 'V1', 'V2', 'V3'];

/**
 * Age group and gender of a result category. Official FFSLC codes are a discipline prefix
 * (C, V, T, CM, CP…), the gender (H/F) and the age group: CHS, CFM, VFV2, CMHJ. Older imports
 * also use SEH/SEF, HS/FS or V1H. The discipline letter must not be read as an age group: in
 * "CFM" the C is canicross and M is minime. Unknown codes fall back to the profile gender and
 * to the senior group.
 */
export function memberCategory(
  category: string | null | undefined,
  profileGender: string | null | undefined,
): { ageGroup: MemberAgeGroup; gender: 'homme' | 'femme' | null } {
  const code = normalizeStandingCategory(category || '');
  let codeGender: string | undefined;
  let ageGroup: MemberAgeGroup = 'S';
  const official = code.match(/^H?(?:CM|CP|CT|C|V|T)?([HF])(V?[123]|BB|[SJCMBPE])$/);
  const reversed = code.match(/^(V[123]|SE)([HF])$/);
  if (official) {
    codeGender = official[1];
    // "VF1" abbreviates "VFV1".
    ageGroup = (/^[123]$/.test(official[2]) ? `V${official[2]}` : official[2]) as MemberAgeGroup;
  } else if (reversed) {
    codeGender = reversed[2];
    ageGroup = reversed[1] === 'SE' ? 'S' : reversed[1] as MemberAgeGroup;
  }
  const gender = codeGender === 'H' ? 'homme'
    : codeGender === 'F' ? 'femme'
    : profileGender === 'Male' ? 'homme'
    : profileGender === 'Female' ? 'femme'
    : null;
  return { ageGroup, gender };
}

export function groupClubActivity(results: Result[]) {
  const events = new Map<string, {
    id: string; name: string; date: string; people: Set<string>;
    disciplines: Map<string, Set<string>>;
    medals: Map<string, { discipline: string; category: string; ranking: 'category' | 'overall'; gold: number; silver: number; bronze: number; chocolate: number; winners: { id: string; humanId: string; name: string; rank: number }[] }>;
  }>();
  for (const result of results) {
    const id = result.event?.id || result.eventId || result.race?.eventId;
    if (!id) continue;
    let event = events.get(id);
    if (!event) {
      event = { id, name: result.event?.name || '', date: result.event?.date || '', people: new Set(), disciplines: new Map(), medals: new Map() };
      events.set(id, event);
    }
    const humanId = result.team?.human?.id || result.team?.humanId;
    if (!humanId) continue;
    event.people.add(humanId);
    const discipline = normalizeResultDiscipline(result.discipline || result.race?.discipline, result.race?.name, result.category)
      || result.discipline || result.race?.discipline || 'other';
    if (!event.disciplines.has(discipline)) event.disciplines.set(discipline, new Set());
    event.disciplines.get(discipline)!.add(humanId);
    const rank = medalRank(result);
    if (rank && rank >= 1 && rank <= 4) {
      const category = normalizeStandingCategory(result.category || '');
      const ranking = 'category';
      const key = JSON.stringify([discipline, category, ranking]);
      if (!event.medals.has(key)) event.medals.set(key, { discipline, category, ranking, gold: 0, silver: 0, bronze: 0, chocolate: 0, winners: [] });
      const medals = event.medals.get(key)!;
      medals.winners.push({ id: result.id, humanId, name: result.team?.human?.name || '', rank });
      if (rank === 1) medals.gold++;
      else if (rank === 2) medals.silver++;
      else if (rank === 3) medals.bronze++;
      else medals.chocolate++;
    }
  }
  return [...events.values()].sort((a, b) => b.date.localeCompare(a.date)).map(event => ({
    id: event.id, name: event.name, date: event.date, participants: event.people.size,
    disciplines: [...event.disciplines].map(([discipline, people]) => ({ discipline, participants: people.size })),
    medals: [...event.medals].map(([key, medals]) => ({ key, ...medals, winners: [...medals.winners].sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name)) })),
    disciplineMedals: [...event.disciplines.keys()].map(discipline => {
      const total = { discipline, gold: 0, silver: 0, bronze: 0, chocolate: 0 };
      for (const medals of event.medals.values()) {
        if (medals.discipline !== discipline) continue;
        total.gold += medals.gold;
        total.silver += medals.silver;
        total.bronze += medals.bronze;
        total.chocolate += medals.chocolate;
      }
      return total;
    }).filter(total => total.gold + total.silver + total.bronze + total.chocolate > 0),
  }));
}
